import { HttpStatusCode } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { pairwise } from 'rxjs';

import {
  JobStatusUtils,
  JobStatusV4,
  OptimizationResult
} from '@core/models/optimization.model';
import { OptimizationService } from '@core/services/optimization/optimization.service';
import { WebsocketService } from '@core/services/websocket/websocket.service';
import * as PlanActions from '@core/store/plan/plan.actions';

const GONE: readonly number[] = [HttpStatusCode.Forbidden, HttpStatusCode.NotFound];

export const UNTRACKED_RUNS_KEPT = 50;

export interface AssistantRunState {
  status: JobStatusV4;
  outputPlanId?: string;
  /** Unset when the end was only learned after a reconnect: GBE does not say when, within the outage, it was. */
  endedAt?: number;
}

/**
 * Follows the optimisations the assistant started, from the same websocket action that keeps the
 * plan page's launched-optimizations table live. Statuses are also kept for the newest untracked
 * jobs, because the first one can arrive before the assistant's own stream announces the run.
 */
@Injectable({ providedIn: 'root' })
export class AssistantRunsService {
  private readonly optimizations = inject(OptimizationService);
  private readonly states = signal<ReadonlyMap<string, AssistantRunState>>(new Map());
  private readonly tracked = new Set<string>();
  readonly disconnectedSince = signal<number | null>(null);

  constructor() {
    inject(Actions)
      .pipe(ofType(PlanActions.optimizationUpdated), takeUntilDestroyed())
      .subscribe(({ result }) => this.record(result, Date.now()));
    // The socket does not replay what it missed while down, so a run may have finished meanwhile.
    inject(WebsocketService)
      .connected$.pipe(pairwise(), takeUntilDestroyed())
      .subscribe(([was, is]) => {
        if (was && !is) this.disconnectedSince.set(Date.now());
        if (!was && is) {
          this.disconnectedSince.set(null);
          this.refreshUnfinished();
        }
      });
  }

  state(jobId: string): AssistantRunState | undefined {
    return this.states().get(jobId);
  }

  track(jobId: string): void {
    this.tracked.add(jobId);
  }

  // For a run started before a page refresh: the socket does not replay the statuses sent meanwhile.
  follow(jobId: string): void {
    if (this.tracked.has(jobId)) return;
    this.track(jobId);
    this.read(jobId);
  }

  private record(result: OptimizationResult, seenAt?: number): void {
    if (!result.jobId) return;
    const jobId = result.jobId;
    this.states.update((map) => {
      const previous = map.get(jobId);
      const ended = JobStatusUtils.isTerminalStatus(result.status);
      const next = new Map(map);
      next.set(jobId, {
        status: result.status,
        outputPlanId: result.outputPlanId ?? previous?.outputPlanId,
        endedAt: previous?.endedAt ?? (ended ? seenAt : undefined)
      });
      this.dropOldestUntracked(next);
      return next;
    });
  }

  // The socket also carries every other job the user can see, the whole platform for a super user
  // on the history page; only the few newest can still be a run the assistant is about to announce.
  private dropOldestUntracked(map: Map<string, AssistantRunState>): void {
    const untracked = [...map.keys()].filter((jobId) => !this.tracked.has(jobId));
    untracked
      .slice(0, Math.max(0, untracked.length - UNTRACKED_RUNS_KEPT))
      .forEach((jobId) => map.delete(jobId));
  }

  private refreshUnfinished(): void {
    for (const jobId of this.tracked) {
      const state = this.states().get(jobId);
      if (state && JobStatusUtils.isTerminalStatus(state.status)) continue;
      this.read(jobId);
    }
  }

  private read(jobId: string): void {
    this.optimizations.getOptimization$(jobId).subscribe({
      next: (r) => this.record(r),
      // A deleted run, or one out of the user's reach, would otherwise be read again on every reconnect.
      // gbeErrorInterceptor rethrows the parsed GBE error, which keeps the HTTP status.
      error: (err: { status?: number } | null) => {
        if (GONE.includes(err?.status ?? 0)) this.tracked.delete(jobId);
      }
    });
  }
}
